import { type AppDispatch } from 'common/hooks/redux'
import { mergeDefined } from 'common/utils/mergeDefined'
import { type IPatientStatus } from 'features/Home/interfaces/IInfoSchedule'
import { getActiveWeekQueries } from 'features/Schedule/api/optimistics.updates'
import { scheduleApi } from 'features/Schedule/api/schedule.api'
import type { ScheduleDay, ScheduleWeek } from 'features/Schedule/api/schedule.types'
import {
  isRedundantPusherPatientUpsert,
  recordScheduleMutationUpsert,
} from 'features/Schedule/pusher/schedulePusherEcho'
import { type PatientAppointmentType } from 'features/Schedule/pusher/type'
import _ from 'lodash'

export const SCHEDULE_PUSHER_BATCH_DEBOUNCE_MS = 7000

export type SchedulePusherPendingBatch = {
  patientUpdates: Map<number, PatientAppointmentType>
  patientCreates: Map<number, PatientAppointmentType>
  patientDeletes: Set<number>
  locationUpdates: Map<number, number>
  statusUpdates: Map<number, { status_code: number; temperature: number }>
  roomStatusUpdates: Map<number, number>
}

export const createEmptySchedulePusherBatch = (): SchedulePusherPendingBatch => ({
  patientUpdates: new Map(),
  patientCreates: new Map(),
  patientDeletes: new Set(),
  locationUpdates: new Map(),
  statusUpdates: new Map(),
  roomStatusUpdates: new Map(),
})

export const isSchedulePusherBatchEmpty = (batch: SchedulePusherPendingBatch): boolean => {
  return (
    batch.patientUpdates.size === 0 &&
    batch.patientCreates.size === 0 &&
    batch.patientDeletes.size === 0 &&
    batch.locationUpdates.size === 0 &&
    batch.statusUpdates.size === 0 &&
    batch.roomStatusUpdates.size === 0
  )
}

const applyPatientUpsertToDraft = (
  draft: ScheduleDay | ScheduleWeek,
  patientData: PatientAppointmentType,
  currentViewDate: string,
) => {
  if (!draft.items) return
  if (isRedundantPusherPatientUpsert(patientData)) return

  const currentUrlDate = window.location.pathname.split('/').pop() || currentViewDate
  if (currentUrlDate !== patientData.date) {
    const deleteIndex = draft.items.findIndex((item) => item.id === patientData.id)
    if (deleteIndex !== -1) {
      draft.items.splice(deleteIndex, 1)
    }
    return
  }

  const room = draft.rooms.find((r) => r.value === patientData.room.code)
  const originalIndex = draft.items.findIndex((item) => item.id === patientData.id)

  if (originalIndex !== -1) {
    const existing = draft.items[originalIndex]
    const newObj = {
      ...mergeDefined(
        existing as unknown as Record<string, unknown>,
        patientData as unknown as Record<string, unknown>,
      ),
      room: room ?? existing.room,
    } as typeof existing

    if (!_.isEqual(draft.items[originalIndex], newObj)) {
      draft.items.splice(originalIndex, 1, newObj)
      recordScheduleMutationUpsert(patientData)
    }
    return
  }

  draft.items.push({
    ...patientData,
    room,
  })
  recordScheduleMutationUpsert(patientData)

  if ('has_ehr_booking' in draft && !patientData.emr_updated_at) {
    draft.has_ehr_booking = true
  }
}

const applyBatchToDayDraft = (
  draft: ScheduleDay,
  batch: SchedulePusherPendingBatch,
  viewDate: string,
) => {
  if (!draft?.items) return

  batch.patientDeletes.forEach((appointmentId) => {
    const index = draft.items.findIndex((item) => item.id === appointmentId)
    if (index !== -1) {
      draft.items.splice(index, 1)
    }
  })

  batch.patientCreates.forEach((patientData, appointmentId) => {
    if (batch.patientDeletes.has(appointmentId)) return
    applyPatientUpsertToDraft(draft, patientData, viewDate)
  })

  batch.patientUpdates.forEach((patientData, appointmentId) => {
    if (batch.patientDeletes.has(appointmentId)) return
    applyPatientUpsertToDraft(draft, patientData, viewDate)
  })

  batch.locationUpdates.forEach((roomId, appointmentId) => {
    if (batch.patientDeletes.has(appointmentId)) return
    const room = draft.rooms.find((r) => r.id === roomId)
    const index = draft.items.findIndex((item) => item.id === appointmentId)
    if (index !== -1 && room) {
      const nextItem = { ...draft.items[index], room }
      if (!_.isEqual(draft.items[index], nextItem)) {
        draft.items[index] = nextItem
      }
    }
  })

  batch.statusUpdates.forEach(({ status_code, temperature }, appointmentId) => {
    if (batch.patientDeletes.has(appointmentId)) return
    const status = draft.patient_statuses.find((s) => s.code === status_code)
    if (!status) return
    const index = draft.items.findIndex((item) => item.id === appointmentId)
    if (index !== -1) {
      const nextItem = {
        ...draft.items[index],
        status: status as IPatientStatus,
        temperature,
      }
      if (!_.isEqual(draft.items[index], nextItem)) {
        draft.items[index] = nextItem
      }
    }
  })

  batch.roomStatusUpdates.forEach((roomStatusCode, roomId) => {
    const roomIndex = draft.rooms.findIndex((room) => room.id === roomId)
    if (roomIndex === -1) return
    const status = draft.room_statuses.find((s) => s.code === roomStatusCode)
    const nextRoom = {
      ...draft.rooms[roomIndex],
      status,
    }
    if (!_.isEqual(draft.rooms[roomIndex], nextRoom)) {
      draft.rooms[roomIndex] = nextRoom
    }
  })
}

const applyBatchToWeekDraft = (
  draft: ScheduleWeek,
  batch: SchedulePusherPendingBatch,
  viewDate: string,
) => {
  if (!draft?.items) return

  batch.patientDeletes.forEach((appointmentId) => {
    const index = draft.items.findIndex((item) => item.id === appointmentId)
    if (index !== -1) {
      draft.items.splice(index, 1)
    }
  })

  batch.patientCreates.forEach((patientData, appointmentId) => {
    if (batch.patientDeletes.has(appointmentId)) return
    applyPatientUpsertToDraft(draft, patientData, viewDate)
  })

  batch.patientUpdates.forEach((patientData, appointmentId) => {
    if (batch.patientDeletes.has(appointmentId)) return
    applyPatientUpsertToDraft(draft, patientData, viewDate)
  })

  batch.locationUpdates.forEach((roomId, appointmentId) => {
    if (batch.patientDeletes.has(appointmentId)) return
    const room = draft.rooms.find((r) => r.id === roomId)
    const index = draft.items.findIndex((item) => item.id === appointmentId)
    if (index !== -1 && room) {
      const nextItem = { ...draft.items[index], room }
      if (!_.isEqual(draft.items[index], nextItem)) {
        draft.items[index] = nextItem
      }
    }
  })

  batch.statusUpdates.forEach(({ status_code, temperature }, appointmentId) => {
    if (batch.patientDeletes.has(appointmentId)) return
    const status = draft.patient_statuses.find((s) => s.code === status_code)
    if (!status) return
    const index = draft.items.findIndex((item) => item.id === appointmentId)
    if (index !== -1) {
      const nextItem = {
        ...draft.items[index],
        status: status as IPatientStatus,
        temperature,
      }
      if (!_.isEqual(draft.items[index], nextItem)) {
        draft.items[index] = nextItem
      }
    }
  })
}

export const applySchedulePusherBatch = (
  dispatch: AppDispatch,
  siteId: string,
  viewDate: string,
  batch: SchedulePusherPendingBatch,
  scheduleQueries: Record<string, unknown>,
) => {
  if (isSchedulePusherBatchEmpty(batch)) return

  dispatch(
    scheduleApi.util.updateQueryData('fetchScheduleDay', { siteId, date: viewDate }, (draft) => {
      applyBatchToDayDraft(draft, batch, viewDate)
    }) as any,
  )

  const weekRanges = getActiveWeekQueries(scheduleQueries, siteId, viewDate)
  weekRanges.forEach(({ startDate, endDate }) => {
    dispatch(
      scheduleApi.util.updateQueryData(
        'fetchScheduleWeek',
        { siteId, startDate, endDate },
        (draft) => {
          applyBatchToWeekDraft(draft, batch, viewDate)
        },
      ) as any,
    )
  })
}

export const mergePatientUpdateIntoBatch = (
  batch: SchedulePusherPendingBatch,
  data: PatientAppointmentType,
) => {
  batch.patientDeletes.delete(data.id)
  batch.patientCreates.delete(data.id)
  batch.patientUpdates.set(data.id, data)
}

export const mergePatientCreateIntoBatch = (
  batch: SchedulePusherPendingBatch,
  data: PatientAppointmentType,
) => {
  if (batch.patientDeletes.has(data.id)) return
  batch.patientUpdates.delete(data.id)
  batch.patientCreates.set(data.id, data)
}

export const mergePatientDeleteIntoBatch = (
  batch: SchedulePusherPendingBatch,
  appointmentId: number,
) => {
  batch.patientUpdates.delete(appointmentId)
  batch.patientCreates.delete(appointmentId)
  batch.locationUpdates.delete(appointmentId)
  batch.statusUpdates.delete(appointmentId)
  batch.patientDeletes.add(appointmentId)
}

export const mergePatientLocationIntoBatch = (
  batch: SchedulePusherPendingBatch,
  appointmentId: number,
  roomId: number,
) => {
  if (batch.patientDeletes.has(appointmentId)) return
  batch.locationUpdates.set(appointmentId, roomId)
}

export const mergePatientStatusIntoBatch = (
  batch: SchedulePusherPendingBatch,
  appointmentId: number,
  status_code: number,
  temperature: number,
) => {
  if (batch.patientDeletes.has(appointmentId)) return
  batch.statusUpdates.set(appointmentId, { status_code, temperature })
}

export const mergeRoomStatusIntoBatch = (
  batch: SchedulePusherPendingBatch,
  roomId: number,
  roomStatusCode: number,
) => {
  batch.roomStatusUpdates.set(roomId, roomStatusCode)
}
